import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { Sprout, Network, Sparkles, HandMetal } from 'lucide-react';
import { imgUrl, imgSrcSet } from '../lib/api';

const IMG = {
  spores: 'https://images.unsplash.com/photo-1570161387493-1ad88c522eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxtdXNocm9vbSUyMG15Y2VsaXVtJTIwbGFib3JhdG9yeSUyMGRhcmslMjBuZW9uJTIwYmlvdGVjaHxlbnwwfHx8fDE3ODg5NzkwNzZ8MA&ixlib=rb-4.1.0&q=85',
  glow: 'https://images.unsplash.com/photo-1540258521336-5c1489b392e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtdXNocm9vbSUyMG15Y2VsaXVtJTIwbGFib3JhdG9yeSUyMGRhcmslMjBuZW9uJTIwYmlvdGVjaHxlbnwwfHx8fDE3ODg5NzkwNzZ8MA&ixlib=rb-4.1.0&q=85',
  shimeji: 'https://images.unsplash.com/photo-1628237117116-3afb22d021f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4A0MTJ8MHwxfHNlYXJjaHwzfHxnb3VybWV0JTIwbXVzaHJvb20lMjBjdWx0aXZhdGlvbiUyMGxhYm9yYXRvcnklMjBzdWJzdHJhdGV8ZW58MHx8fHwxNzg4OTc5MDg2fDA&ixlib=rb-4.1.0&q=85',
  pleurote: 'https://images.unsplash.com/photo-1719936597105-07b2d0219632?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4A0MTJ8MHwxfHNlYXJjaHwyfHxnb3VybWV0JTIwbXVzaHJvb20lMjBjdWx0aXZhdGlvbiUyMGxhYm9yYXRvcnklMjBzdWJzdHJhdGV8ZW58MHx8fHwxNzg4OTc5MDg2fDA&ixlib=rb-4.1.0&q=85',
};

const STAGES = [
  {
    icon: Sprout,
    day: 'J+0',
    phase: 'INOCULATION',
    title: "L'ORIGINE",
    text: "Une seringue, un geste précis. 10 ml de culture liquide rencontrent 2,5 kg de substrat stérile. Tout commence dans le silence d'une salle blanche ISO-5.",
    stat: { k: 'STÉRILITÉ', v: '99,7 %' },
    image: IMG.spores,
    accent: 'neon',
  },
  {
    icon: Network,
    day: 'J+7',
    phase: 'COLONISATION',
    title: 'LE RÉSEAU',
    text: "Invisible pendant 48 h, le mycélium explose ensuite : un réseau blanc qui digère la sciure à raison de 2 cm par jour. Le bloc devient un organisme unique.",
    stat: { k: 'VITESSE', v: '2 CM/J' },
    image: IMG.glow,
    accent: 'cyber',
  },
  {
    icon: Sparkles,
    day: 'J+14',
    phase: 'PRIMORDIA',
    title: "L'ÉVEIL",
    text: "Choc thermique, lumière 6500 K, humidité 92 %. Des milliers de points blancs perlent à la surface du bloc : les futurs chapeaux se décident.",
    stat: { k: 'HUMIDITÉ', v: '92 %' },
    image: IMG.shimeji,
    accent: 'violet',
  },
  {
    icon: HandMetal,
    day: 'J+21',
    phase: 'RÉCOLTE',
    title: 'LA MOISSON',
    text: "Un quart de tour, un léger craquement. 850 g de pleurotes roses par bloc — puis le cycle recommence, pour un deuxième, puis un troisième flush.",
    stat: { k: 'RÉCOLTE', v: '850 G' },
    image: IMG.pleurote,
    accent: 'neon',
  },
];

const ACCENTS = {
  neon: { text: 'text-neon', bar: 'bg-neon', chip: 'border-neon/40 text-neon' },
  cyber: { text: 'text-cyber', bar: 'bg-cyber', chip: 'border-cyber/40 text-cyber' },
  violet: { text: 'text-bioviolet', bar: 'bg-bioviolet', chip: 'border-bioviolet/40 text-bioviolet' },
};

const GrowStory = () => {
  const ref = useRef(null);
  const [stage, setStage] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const smooth = useSpring(scrollYProgress, { stiffness: 170, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    return scrollYProgress.on('change', (v) => {
      setStage(Math.min(STAGES.length - 1, Math.floor(v * STAGES.length)));
    });
  }, [scrollYProgress]);

  const s = STAGES[stage];
  const a = ACCENTS[s.accent];

  const jumpTo = (i) => {
    const el = ref.current;
    if (!el) return;
    const top = el.offsetTop + (el.offsetHeight - window.innerHeight) * (i / (STAGES.length - 1));
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <section ref={ref} id="culture" data-testid="grow-story" className="relative h-[260vh]">
      <div className="sticky top-0 flex h-[100dvh] flex-col overflow-hidden bg-void">
        <AnimatePresence initial={false}>
          <motion.div
            key={stage}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 0.35, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <img src={imgUrl(s.image, 1024)} srcSet={imgSrcSet(s.image)} sizes="100vw" alt={s.phase} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgb(var(--void))_15%,transparent_70%)]" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 items-center px-4 sm:px-6 lg:px-8">
          <div className="grid w-full gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-slate-500">
                <span className="inline-block h-px w-10 bg-neon/60" />
                La vie d'un bloc — faites défiler pour cultiver
              </p>

              <AnimatePresence mode="wait">
                <motion.div
                  key={stage}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: -16, transition: { duration: 0.18 } }}
                  variants={{ show: { transition: { staggerChildren: 0.05 } } }}
                >
                  <motion.div
                    variants={{ hidden: { opacity: 0, x: -24 }, show: { opacity: 1, x: 0, transition: { duration: 0.35 } } }}
                    className="mb-4 flex items-center gap-4"
                  >
                    <span className={`clip-frame-sm flex h-12 w-12 items-center justify-center border ${a.chip} bg-void/70 backdrop-blur`}>
                      <s.icon size={22} strokeWidth={1.6} />
                    </span>
                    <div>
                      <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${a.text}`}>{s.day} · {s.phase}</p>
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600">Étape {stage + 1} / {STAGES.length}</p>
                    </div>
                  </motion.div>

                  <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-[-0.02em] sm:text-6xl lg:text-7xl" data-testid="grow-story-title">
                    <span className="block overflow-hidden">
                      <motion.span
                        className={`block ${a.text} text-glow`}
                        variants={{ hidden: { y: '110%' }, show: { y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } }}
                      >
                        {s.title}
                      </motion.span>
                    </span>
                  </h2>

                  <motion.p
                    variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                    className="mt-6 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base"
                    data-testid="grow-story-text"
                  >
                    {s.text}
                  </motion.p>

                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                    className="clip-frame-sm mt-8 inline-flex items-center gap-4 border border-white/10 bg-void/70 px-5 py-3 backdrop-blur"
                    data-testid="grow-story-stat"
                  >
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{s.stat.k}</span>
                    <span className={`font-display text-2xl font-extrabold ${a.text}`}>{s.stat.v}</span>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="hidden items-center justify-end lg:col-span-5 lg:flex">
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={stage}
                  initial={{ opacity: 0, scale: 0.9, rotate: 1.5 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.95, rotate: -1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="clip-frame scanlines relative w-full max-w-md overflow-hidden border border-white/15"
                >
                  <img src={imgUrl(s.image, 720)} srcSet={imgSrcSet(s.image)} sizes="(min-width: 1024px) 33vw, 100vw" alt={s.phase} loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover" data-testid="grow-story-image" />
                  <div className="absolute inset-0 bg-gradient-to-t from-void/85 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 glass-panel border border-white/10 px-4 py-2.5">
                    <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${a.text}`}>{s.day}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">{s.phase}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/8 bg-void/70 backdrop-blur-md">
          <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              {STAGES.map((st, i) => (
                <button
                  key={st.phase}
                  onClick={() => jumpTo(i)}
                  data-testid={`grow-story-step-${i}`}
                  aria-label={st.phase}
                  className="group flex min-h-[24px] min-w-[24px] items-center gap-2 py-2 pr-1"
                >
                  <span className={`h-2 w-2 rounded-full transition-all ${i === stage ? `${ACCENTS[st.accent].bar} scale-125` : i < stage ? 'bg-neon/50' : 'bg-white/15 group-hover:bg-white/35'}`} />
                  <span className={`hidden font-mono text-[9px] uppercase tracking-[0.2em] transition-colors sm:block ${i === stage ? ACCENTS[st.accent].text : 'text-slate-600 group-hover:text-slate-400'}`}>
                    {st.day}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative h-0.5 flex-1 overflow-hidden bg-white/10">
              <motion.div className="absolute inset-y-0 left-0 bg-neon" style={{ width: smooth.get() ? undefined : '0%', scaleX: smooth, transformOrigin: 'left', right: 0 }} />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="grow-story-phase-label">
              {s.phase}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrowStory;
